/**
 * La carte « Interventions prévues » de la fiche d'une machine (ADR-0016 ; née de la carte « Visites prévues » du lot PL1a) :
 * ce qui est déjà au planning pour elle, et le geste pour y mettre une intervention sans quitter la fiche.
 *
 * Une seule source (convention n° 9) : les interventions vivent dans le planning, la fiche les LIT — elle n'en garde aucune
 * copie. « Planifier une intervention » ouvre le volet de création déjà prérempli avec cette machine et son site ; la machine
 * n'a donc jamais à être cherchée une seconde fois.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type Asset, type ListeInterventionsDeMachine } from '../api/client';
import { useDroitsPlanning } from '../auth/useDroits';
import { aujourdhuiLocal } from '../domaine/interventions';
import { formulaireDepuisMachine, formulaireVide, machineChoisie, refusAjoutMachine } from '../domaine/planning';
import { lienIntervention, numeroAffiche, phraseEquipeDuJour, phraseQuandIntervention } from '../domaine/planning-interventions';
import { VoletIntervention } from './VoletIntervention';

export function InterventionsPrevuesMachine({ machine }: { machine: Asset }) {
  const droits = useDroitsPlanning();
  const [interventions, setInterventions] = useState<ListeInterventionsDeMachine | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [voletIntervention, setVoletIntervention] = useState(false);
  const [succes, setSucces] = useState<string | null>(null);
  const [rechargement, setRechargement] = useState(0);

  useEffect(() => {
    if (!droits.peutLirePlanning) return;
    let annule = false;
    api
      .interventionsDeMachine(machine.id)
      .then((r) => {
        if (annule) return;
        setInterventions(r);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Les interventions de cette machine n’ont pas pu être lues.');
      });
    return () => {
      annule = true;
    };
  }, [machine.id, droits.peutLirePlanning, rechargement]);

  const apresCreationIntervention = useCallback(() => {
    setVoletIntervention(false);
    setSucces('Intervention créée : elle est au planning.');
    setRechargement((n) => n + 1);
  }, []);

  if (!droits.peutLirePlanning) return null;

  const choisie = machineChoisie(machine);
  // Une machine sans site ne peut pas recevoir d'intervention : elle est un déplacement vers un site.
  const sansSite = refusAjoutMachine(formulaireVide(), choisie);

  return (
    <div className="carte">
      <h2>Interventions prévues</h2>
      <div className="carte-corps">
        {succes && <div className="message succes">{succes}</div>}
        {erreur && <div className="message erreur">{erreur}</div>}
        {interventions === null && !erreur && <p className="aide">Chargement…</p>}
        {interventions !== null && interventions.items.length === 0 && <p className="aide">Aucune intervention prévue pour cette machine.</p>}
        {interventions !== null && interventions.items.length > 0 && (
          <ul className="suites-machine">
            {interventions.items.map((i) => (
              <li key={i.id}>
                <Link to={lienIntervention(i.id, i.prochainJour)}>
                  <strong>{phraseQuandIntervention(i, interventions.today)}</strong>
                </Link>
                {i.natureLabel ? ` — ${i.natureLabel}` : ''}
                {i.enRetard && <span className="etat revoque"> en retard</span>}
                {i.tentative && <span className="etat suspendu"> à confirmer</span>}
                <div className="aide">
                  {phraseEquipeDuJour(i.technicians)} · {numeroAffiche(i)}
                </div>
              </li>
            ))}
          </ul>
        )}
        {interventions !== null && interventions.total > interventions.items.length && (
          <p className="aide">
            {interventions.items.length} interventions affichées sur {interventions.total}.
          </p>
        )}

        {droits.peutGererPlanning &&
          (sansSite === null ? (
            <div className="saisie-ligne">
              <button type="button" className="secondaire" onClick={() => setVoletIntervention(true)}>
                Planifier une intervention
              </button>
            </div>
          ) : (
            <p className="aide">{sansSite}</p>
          ))}
        <p className="aide">
          Clique une intervention pour ouvrir son briefing dans le <Link to="/planning/interventions">planning des interventions</Link> : c’est là qu’on la modifie, qu’on l’affecte, qu’on la déplace et qu’on saisit son bon.
        </p>
      </div>

      {voletIntervention && (
        <VoletIntervention
          depart={formulaireDepuisMachine(choisie, aujourdhuiLocal())}
          onCreeeIntervention={apresCreationIntervention}
          onFermer={() => setVoletIntervention(false)}
        />
      )}
    </div>
  );
}
